import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { convertYouTubeToMp3 } from '../services/api';
import Header from '../components/Layout/Header';

const ProgressPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('Rozpoczynanie...');
  const conversionDone = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    let isActive = true;

    const url = location.state?.url;
    console.log('[ProgressPage] Component mounted with URL:', url);
    if (!url) {
      console.log('[ProgressPage] No URL provided, redirecting to home');
      navigate('/');
      return;
    }

    // Fake progress bar - rośnie do 90% w ciągu ~25 sekund
    let fakeProgress = 0;
    const statuses = [
      { at: 0, text: 'Łączenie z YouTube...' },
      { at: 15, text: 'Pobieranie audio...' },
      { at: 40, text: 'Konwertowanie do MP3...' },
      { at: 70, text: 'Finalizowanie...' },
    ];

    intervalRef.current = setInterval(() => {
      if (conversionDone.current) {
        clearInterval(intervalRef.current);
        return;
      }
      
      // Rośnie szybciej na początku, zwalnia pod koniec
      if (fakeProgress < 50) {
        fakeProgress += 2;
      } else if (fakeProgress < 80) {
        fakeProgress += 1;
      } else if (fakeProgress < 90) {
        fakeProgress += 0.3;
      }
      
      fakeProgress = Math.min(fakeProgress, 90);
      setProgress(Math.round(fakeProgress));
      
      // Aktualizuj status
      for (let i = statuses.length - 1; i >= 0; i--) {
        if (fakeProgress >= statuses[i].at) {
          setStatus(statuses[i].text);
          break;
        }
      }
    }, 500);

    console.log('[ProgressPage] Starting conversion for:', url);
    
    // Start conversion
    convertYouTubeToMp3(url, () => {})
      .then((response) => {
        if (!isActive) return;
        console.log('[ProgressPage] Conversion complete:', response);
        conversionDone.current = true;
        clearInterval(intervalRef.current);
        setProgress(100);
        setStatus('Gotowe!');
        setTimeout(() => {
          if (!isActive) return;
          console.log('[ProgressPage] Navigating to done page');
          navigate('/done', { state: { result: response } });
        }, 500);
      })
      .catch((error) => {
        if (!isActive) return;
        console.error('[ProgressPage] Conversion error:', error);
        conversionDone.current = true;
        clearInterval(intervalRef.current);
        navigate('/error', { state: { error: error }, replace: true });
      });

    return () => {
      isActive = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [location, navigate]);

  const handleCancel = () => {
    console.log('[ProgressPage] User cancelled conversion');
    navigate('/');
  };

  return (
    <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col w-full overflow-x-hidden">
      <Header />

      <div className="flex-1 flex items-center justify-center p-4 w-full overflow-x-hidden">
        <div className="w-full max-w-[520px]">
          {/* Logo & Status */}
          <div className="flex flex-col items-center gap-6 mb-10">
            <div className="relative group">
              <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-700"></div>
              <div className="relative w-32 h-32 bg-surface-dark rounded-2xl shadow-2xl flex items-center justify-center overflow-hidden border border-surface-border">
                <img 
                  src="/logo.png" 
                  alt="Logo" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="text-center space-y-2 mt-4">
              <h1 className="text-slate-900 dark:text-white tracking-tight text-[32px] font-bold leading-tight px-4 pb-1">
                Konwerter MP3
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-base font-normal leading-normal px-4">
                Pobieranie danych z YouTube...
              </p>
            </div>
          </div>

          {/* Progress Card */}
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-surface-border shadow-sm p-6 w-full">
            <div className="flex flex-col gap-4">
              {/* Progress Header */}
              <div className="flex gap-6 justify-between items-end">
                <div className="flex flex-col gap-1">
                  <p className="text-slate-900 dark:text-white text-base font-semibold leading-normal">
                    Status operacji
                  </p>
                  <span className="text-xs font-medium text-primary uppercase tracking-wider">
                    {status}
                  </span>
                </div>
                <p className="text-slate-900 dark:text-white text-2xl font-bold leading-none tabular-nums">
                  {progress}%
                </p>
              </div>

              {/* Progress Bar */}
              <div className="rounded-full bg-slate-100 dark:bg-slate-800 h-4 overflow-hidden relative w-full">
                <div
                  className="h-full rounded-full bg-primary relative overflow-hidden transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                </div>
              </div>

              {/* Details */}
              <div className="flex justify-between items-center pt-1">
                <p className="text-slate-500 dark:text-slate-400 text-sm font-normal leading-normal flex items-center gap-1">
                  <span className="material-symbols-outlined text-[16px]">schedule</span>
                  Szacowany czas: 30s
                </p>
              </div>
            </div>
          </div>

          {/* Cancel Button */}
          <div className="flex justify-center mt-8">
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg border border-gray-200 dark:border-surface-border text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-surface-border transition-colors font-medium text-sm group"
            >
              <span className="material-symbols-outlined text-[20px] text-slate-500 dark:text-slate-400 group-hover:text-red-500 transition-colors">
                close
              </span>
              Anuluj konwersję
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressPage;
